import { Box, Chip, FormControl, Stack, Typography } from '@mui/material'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { C1AutocompleteMultipleAsyncHook } from 'components/core/autocomplete'
import { AppAvatar } from 'components/core/avatar'
import { appEntitlementRenderOption } from 'components/core/search/apps'
import { AccessRequest } from 'components/pages/accessrequest/create/form-v2/RequestAccessFormV2'
import { C1EntitlementDisplayName, getEntitlementDisplayNameTextOnly } from 'components/pages/entitlement/display-name'
import { AppEntitlementValue } from 'lib/actions/apps/app-entitlement'
import { SystemAnyAccessEntitlementID } from 'lib/constants/system'
import { RequestCatalogSearchServiceSearchEntitlementsPerAppRequest } from 'pbts/c1/api/requestcatalog/v1/requestcatalog'
import { FC, useMemo } from 'react'
import { Control, Controller, FieldErrors, useWatch } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { searchEntitlementsPerAppInCatalogApi } from 'redux_toolkit/request-catalogs'

type AccessRequestFormEntFieldProps = {
    control: Control<AccessRequest>
    errors: FieldErrors<AccessRequest>
}

const renderTags = (
    value: AppEntitlementValue[],
    getTagProps,
): React.ReactElement[] => value.map((option, index) => <Chip
    sx={{ borderRadius: C1BorderRadius.regular }}
    label={option && <Box py={1}>
        <C1EntitlementDisplayName
            entitlement={option}
            readOnly
            textTruncationWidth="32em"
        />
    </Box>}
    {...getTagProps({ index })}
/>)

const renderLabel = (opt): string => (opt.entitlement
    ? getEntitlementDisplayNameTextOnly(opt.entitlement)
    : '')

const renderOption = (props, opt): React.ReactNode => {
    if (!opt) return null

    if (opt.id === SystemAnyAccessEntitlementID) {
        return <li {...props} key={`${opt.id}-${opt.appId}`}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <AppAvatar appId={opt.appId} appName={opt.app?.displayName} size="xs" />
                <Typography>
                    <FormattedMessage id="AnyAccessEntitlement" defaultMessage="Application access" />
                </Typography>
            </Stack>
        </li>
    }

    return appEntitlementRenderOption(props, opt)
}

const getGroupByValue = (option: AppEntitlementValue): string =>
    (option.id === SystemAnyAccessEntitlementID ? 'Basic app access' : 'All entitlements')

const sortOptionsByGroup = (options: AppEntitlementValue[]): AppEntitlementValue[] => {
    return [...options].sort((a, b) => {
        const groupA = getGroupByValue(a)
        const groupB = getGroupByValue(b)
        return groupB.localeCompare(groupA)
    })
}

type ReqType = RequestCatalogSearchServiceSearchEntitlementsPerAppRequest
export const AccessRequestFormEntField: FC<AccessRequestFormEntFieldProps> = ({
    control,
    errors,
}) => {
    const intl = useIntl()
    const app = useWatch({
        name: 'app',
        control,
    })
    const user = useWatch({
        name: 'user',
        control,
    })

    const disabled = !app?.id || !user?.id
    const targetUserId = user?.id
    const appId = app?.id

    const req = useMemo(() => ({
        create: (args: Partial<ReqType>) => RequestCatalogSearchServiceSearchEntitlementsPerAppRequest.create({
            ...args,
            targetUserId,
            appId,
        }),
    }), [targetUserId, appId])

    if (disabled) return null

    return <FormControl error={!!errors.ents} fullWidth>
        <Controller
            name="ents"
            control={control}
            render={({ field }): React.ReactElement => <C1AutocompleteMultipleAsyncHook<ReqType, AppEntitlementValue>
                id="ents"
                placeholder={intl.formatMessage({ id: 'SelectOneAppEntHook_placeholder', defaultMessage: ' Select an entitlement' })}
                hook={searchEntitlementsPerAppInCatalogApi.useLazySearchCatalogEntitlementsPerApp}
                Req={req}
                label={intl.formatMessage({
                    id: 'component_access_request_entitlements_input_label',
                    defaultMessage: 'What access is needed?',
                })}
                renderOption={renderOption}
                renderTags={renderTags}
                error={!!errors.ents}
                helperText={errors.ents?.message}
                renderLabel={renderLabel}
                onSelected={(v: Array<AppEntitlementValue> | null): void => field.onChange(v)}
                value={(field.value) as unknown as Array<AppEntitlementValue>}
                onBlur={field.onBlur}
                groupBy={getGroupByValue}
                filterOptions={(options): AppEntitlementValue[] => sortOptionsByGroup(options)}
            />
            }
        />
    </FormControl>
}
